"use strict";

exports.__esModule = true;
exports.DevicePropertyElement = DevicePropertyElement;
var _react = _interopRequireWildcard(require("react"));
var _polished = require("polished");
var _styledComponents = require("styled-components");
var _DeviceColorController = require("../../Device/DeviceColorSources/DeviceColorController");
var _DeviceContext = require("../../Device/DeviceContext");
var _TextValue = require("../../Measures/TextValue");
var _common = require("../common");
var _TileContext = require("../common/TileContext");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
// The state tile in a table row: how far it sits in from the cell, its
// corner radius, and how much of the state colour it carries.
var STATE_TILE_INSET = 6;
// Below this a tile has no room for card chrome and shows plain text.
var MIN_CARD_HEIGHT = 30;
var STATE_TILE_RADIUS = 10;
var STATE_TILE_TINT = 0.7;
var FALLBACK_HEADING = 'Archivo, "IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif';
var FALLBACK_SHADOW = "0 1px 2px rgba(29, 36, 41, 0.06), 0 6px 16px rgba(29, 36, 41, 0.08), 0 20px 40px -16px rgba(29, 36, 41, 0.14)";

/**
 * Displays a particular property of a device, e.g. name or uuid.
 */
function DevicePropertyElement(props) {
  var propertyName = props.propertyName;
  var theme = (0, _react.useContext)(_styledComponents.ThemeContext) || {};
  var tile = (0, _react.useContext)(_TileContext.TileContext) || {};
  return /*#__PURE__*/_react["default"].createElement(_DeviceContext.DeviceContext.Consumer, null, function (device) {
    return /*#__PURE__*/_react["default"].createElement(_DeviceColorController.DeviceColorController, _extends({
      deviceUuid: device.uuid
    }, (0, _common.getWidgetColorProps)(props)), function (_ref) {
      var color = _ref.color,
        backgroundColor = _ref.backgroundColor;
      // Inside a device card the property is the card's title line.
      var _getWidgetPositionPro = (0, _common.getWidgetPositionProps)(props),
        width = _getWidgetPositionPro.width,
        height = _getWidgetPositionPro.height;
      // Flat inside a card, or too short to be a card of its own.
      if (tile.flat || height < MIN_CARD_HEIGHT) {
        var isTitle = tile.role !== "subtitle";
        var name = /*#__PURE__*/_react["default"].createElement("span", {
          style: {
            flex: "1 1 auto",
            minWidth: 0,
            lineHeight: 1.2,
            fontFamily: theme.FontFamilyHeading || FALLBACK_HEADING,
            fontSize: isTitle ? tile.flat ? 16 : Math.min(16, Math.max(11, Math.floor(height * 0.6))) : 12,
            fontWeight: isTitle ? 700 : 500,
            letterSpacing: "-0.01em",
            color: isTitle ? color || "#1B2227" : "#7C8A94",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }
        }, device[propertyName]);

        // In a table the name is a rounded tile filled with the state
        // colour, strong enough to read from across the floor.
        if (isTitle && tile.stateFill) {
          var hasState = backgroundColor && backgroundColor !== "transparent";
          return /*#__PURE__*/_react["default"].createElement("div", _extends({
            "data-testid": "DevicePropertyElement"
          }, (0, _common.getWidgetInteractionProps)(props), {
            style: {
              boxSizing: "border-box",
              width: width,
              height: height,
              minWidth: 0,
              padding: STATE_TILE_INSET
            }
          }), /*#__PURE__*/_react["default"].createElement("div", {
            style: {
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              height: "100%",
              minWidth: 0,
              padding: "0 " + (tile.cellPadding || 12) + "px",
              borderRadius: STATE_TILE_RADIUS,
              background: hasState ? (0, _polished.mix)(STATE_TILE_TINT, backgroundColor, "#ffffff") : "#F1F4F6",
              transition: "background .4s ease"
            }
          }, name));
        }
        return /*#__PURE__*/_react["default"].createElement("div", _extends({
          "data-testid": "DevicePropertyElement"
        }, (0, _common.getWidgetInteractionProps)(props), {
          style: {
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: tile.flat ? "flex-start" : "center",
            width: width,
            height: height,
            minWidth: 0
          }
        }), name);
      }

      // A shadow tile, tinted when a state colour is supplied.
      var pad = Math.min(12, Math.floor(Math.min(width, height) / 8));
      var fill = backgroundColor ? (0, _polished.mix)(0.16, backgroundColor, "#ffffff") : "#ffffff";
      return /*#__PURE__*/_react["default"].createElement("div", _extends({
        "data-testid": "DevicePropertyElement"
      }, (0, _common.getWidgetInteractionProps)(props), {
        style: {
          boxSizing: "border-box",
          width: width,
          height: height,
          padding: pad,
          background: fill,
          borderRadius: 12,
          boxShadow: theme.CardShadow || FALLBACK_SHADOW
        }
      }), /*#__PURE__*/_react["default"].createElement(_TextValue.TextValue, _extends({
        value: device[propertyName],
        color: color || "#1B2227",
        width: Math.max(0, width - 2 * pad),
        height: Math.max(0, height - 2 * pad),
        fontWeight: 600
      }, (0, _common.getWidgetFontProps)(props))));
    });
  });
}